import {
  string as yupString,
  number as yupNumber,
} from 'yup';

import InputFormik from '../../shared/forms/input/inputFormik.component';
import SelectFormik from '../../shared/forms/select/sdkSelectFormik.component';
import { required, maxLength, validateValueIsUnique } from '../../shared/forms/validators';
import { decimalWithSignFormatter } from '../../shared/helpers';

const isNonUs = countryCode => countryCode !== 'US';

const requiredWhenNonUS = (_, validator) =>
  validator.when(['countryCode'], {
    is: isNonUs,
    then: yupString().required(),
    otherwise: yupString().nullable(),
  });

const validateLegalEntity = validateValueIsUnique({
  optionsKey: 'legalEntitiesOptions',
  message: 'Legal Entity already exists.',
  conditionFn: (option, selectedValue, context) =>
    option.value === selectedValue && option.legalEntity !== context.parent.legalEntity,
});

const schema = [
  {
    name: 'legalEntity',
    component: InputFormik,
    meta: { label: 'Legal Entity', optionsKey: 'legalEntitiesOptions' },
    options: { disabledWhenEditing: true, yupType: yupString(),
      validationRules: [required, maxLength(6), validateLegalEntity] },
    additionalProps: { required: true },
  },
  {
    name: 'leDescription',
    component: InputFormik,
    meta: { label: 'Legal Entity Description' },
    options: { yupType: yupString(), validationRules: [required, maxLength(240)] },
    additionalProps: { required: true },
  },
  {
    name: 'enabledFlag',
    component: SelectFormik,
    meta: { label: 'Enabled Flag', optionsKey: 'enabledFlagOptions' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'usTaxType',
    component: SelectFormik,
    meta: { label: 'US Tax Type', optionsKey: 'usTaxTypeOptions' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'localTaxType',
    component: SelectFormik,
    meta: { label: 'Local Tax Type', optionsKey: 'localTaxTypeOptions' },
    options: { yupType: yupString(), validationRules: [requiredWhenNonUS] },
    conditions: [{
      when: ({ formik }) => isNonUs(formik.values.countryCode),
      then: {
        additionalProps: { required: true },
      },
      otherwise: {
        additionalProps: { clearable: true },
      },
    }],
  },
  {
    name: 'branch',
    component: SelectFormik,
    meta: { label: 'Branch', optionsKey: 'branchOptions' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'source',
    component: SelectFormik,
    meta: { label: 'Source', optionsKey: 'sourceOptions' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'goldId',
    component: InputFormik,
    meta: { label: 'GOLD ID' },
    options: { yupType: yupString(), validationRules: [maxLength(6)] },
  },
  {
    name: 'countryCode',
    component: SelectFormik,
    meta: { label: 'Country Code', optionsKey: 'countryCodeOptions' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'minorityPercentage',
    component: InputFormik,
    meta: { label: 'Minority Percentage' },
    options: { yupType: yupNumber(), validationRules: [required] },
    additionalProps: { required: true, suffix: '%', valueFormatter: decimalWithSignFormatter },
  },
  ...([...Array(10)].flatMap((_, index) => [
    {
      name: `parent${index + 1}`,
      component: InputFormik,
      meta: { label: `Parent${index + 1}` },
      options: { yupType: yupString(), validationRules: [maxLength(6)] },
    },
    {
      name: `ownership${index + 1}`,
      component: InputFormik,
      meta: { label: `Ownership${index + 1}` },
      options: { yupType: yupString(), validationRules: [maxLength(12)] },
      additionalProps: { suffix: '%', valueFormatter: decimalWithSignFormatter },
    },
  ])),
];

export default schema;
